import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { type VariantProps, cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';
import { backdropMotion, modalPopupMotion } from '../lib/motion';
import { titleVariants } from './title';

/**
 * 모달. 접근성(포커스 트랩·ESC·스크롤 잠금·aria)은 Base UI가 담당하고
 * 여기서는 스타일과 API 표면만 정의한다.
 *
 * 사용:
 *   <Dialog.Root>
 *     <Dialog.Trigger render={<Button>열기</Button>} />
 *     <Dialog.Content title="온보딩">…</Dialog.Content>
 *   </Dialog.Root>
 */
export const DialogRoot = BaseDialog.Root;
export const DialogTrigger = BaseDialog.Trigger;
export const DialogClose = BaseDialog.Close;

export function DialogBackdrop({
  className,
  ...props
}: ComponentProps<typeof BaseDialog.Backdrop>) {
  return (
    <BaseDialog.Backdrop
      className={cn(
        // z는 D2 §7.3의 층 정의를 따른다.
        'bg-overlay z-overlay backdrop-blur-scrim fixed inset-0',
        backdropMotion,
        className,
      )}
      {...props}
    />
  );
}

/** 너비만 바뀐다 — 기본은 `lg`(32rem), 확인 대화상자류는 `sm`/`md`로 좁힌다. */
export const dialogPopupVariants = cva(
  cn(
    'z-modal fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
    // D1 §5: 모달은 16px. 그림자를 쓰는 네 곳 중 하나다(§M1).
    'border-line bg-surface-raised shadow-modal p-panel rounded-2xl border',
    modalPopupMotion,
  ),
  {
    variants: {
      size: {
        sm: 'w-[min(92vw,26rem)]',
        md: 'w-[min(92vw,28rem)]',
        lg: 'w-[min(92vw,32rem)]',
      },
    },
    defaultVariants: { size: 'lg' },
  },
);

export interface DialogPopupProps
  extends ComponentProps<typeof BaseDialog.Popup>, VariantProps<typeof dialogPopupVariants> {}

export function DialogPopup({ className, size, ...props }: DialogPopupProps) {
  return <BaseDialog.Popup className={cn(dialogPopupVariants({ size }), className)} {...props} />;
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof BaseDialog.Title>) {
  return (
    <BaseDialog.Title className={cn(titleVariants({ level: 'card' }), className)} {...props} />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof BaseDialog.Description>) {
  return (
    <BaseDialog.Description className={cn('text-ink-muted mt-1 text-sm', className)} {...props} />
  );
}

/** 대화상자 하단의 버튼 행. 보통 `Dialog.Close` + 확정 버튼 순서로 넣는다. */
export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('mt-card gap-snug flex justify-end', className)} {...props} />;
}

export interface DialogContentProps
  extends ComponentProps<typeof BaseDialog.Popup>, VariantProps<typeof dialogPopupVariants> {
  title?: string;
  description?: string;
}

/** Portal + Backdrop + Popup + 제목/설명을 한 번에 감싸는 편의 컴포넌트. */
export function DialogContent({
  title,
  description,
  size,
  children,
  ...props
}: DialogContentProps) {
  return (
    <BaseDialog.Portal>
      <DialogBackdrop />
      <DialogPopup size={size} {...props}>
        {title ? <DialogTitle>{title}</DialogTitle> : null}
        {description ? <DialogDescription>{description}</DialogDescription> : null}
        {children}
      </DialogPopup>
    </BaseDialog.Portal>
  );
}

export const Dialog = {
  Root: DialogRoot,
  Trigger: DialogTrigger,
  Close: DialogClose,
  Content: DialogContent,
  Backdrop: DialogBackdrop,
  Popup: DialogPopup,
  Title: DialogTitle,
  Description: DialogDescription,
  Footer: DialogFooter,
};
